import {
  Button,
  Checkbox,
  FieldGroup,
  FieldWrap,
  PageHeader,
  RadioGroup,
  TextField
} from '@deque/cauldron-react';
import type { RadioItem } from '@deque/cauldron-react/lib/components/RadioGroup';
import type { ReactElement } from 'react';
import React from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import styles from './OnboardingForm.css';

interface CheckboxOption {
  value: string;
  label: string;
  formValue?: string;
}

interface CheckboxGroupProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  options: CheckboxOption[];
  fieldId: string;
}

export const CheckboxGroup = <T extends FieldValues>({
  control,
  name,
  options,
  fieldId
}: CheckboxGroupProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const currentValue = e.target.value;
          const currentFieldValue = field.value;

          if (e.target.checked) {
            field.onChange(
              Array.from(new Set([...currentFieldValue, currentValue]))
            );
          } else {
            field.onChange(
              currentFieldValue.filter(
                (fieldValue: string) => fieldValue !== currentValue
              )
            );
          }
        };

        return (
          <div className={styles.checkboxGroup}>
            {options.map(({ value, label }) => (
              <Checkbox
                key={value}
                id={`${fieldId}-${value}`}
                label={label}
                value={value}
                checked={field.value.includes(value)}
                onChange={e => handleChange(e)}
              />
            ))}
          </div>
        );
      }}
    />
  );
};

interface OnboardingFormProps {
  onSubmit: (data: OnboardingForm) => void;
  submitting: boolean;
}

export interface OnboardingForm {
  productName: string;
  teamName: string;
  onOneCiCd: string;
  frontend: string[];
  otherFrontend: string;
  ide: string[];
  otherIde: string;
  sourceControl: string[];
  otherSourceControl: string;
  sourceControlProvider: string[];
  otherSourceControlProvider: string;
  testingFramework: string[];
  otherTestingFramework: string;
  ciCdPlatform: string[];
  otherCiCdPlatform: string;
}

const OnboardingForm = ({
  onSubmit,
  submitting
}: OnboardingFormProps): ReactElement => {
  const { t } = useTranslation();
  const onOneCiCdRef = React.useRef<HTMLDivElement>(null);
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<OnboardingForm>({
    defaultValues: {
      productName: '',
      teamName: '',
      onOneCiCd: '',
      frontend: [],
      otherFrontend: '',
      ide: [],
      otherIde: '',
      sourceControl: [],
      otherSourceControl: '',
      sourceControlProvider: [],
      otherSourceControlProvider: '',
      testingFramework: [],
      otherTestingFramework: '',
      ciCdPlatform: [],
      otherCiCdPlatform: ''
    }
  });

  const onOneCiCdOptions: RadioItem[] = [
    {
      id: 'onOneCiCd-yes',
      label: t('Yes'),
      value: 'yes'
    },
    {
      id: 'onOneCiCd-no',
      label: t('No'),
      value: 'no'
    }
  ];

  const handleFormSubmit = (data: OnboardingForm) => {
    onSubmit(data);
  };

  const handleInvalidSubmit = () => {
    if (errors.onOneCiCd && onOneCiCdRef.current) {
      onOneCiCdRef.current.focus();
    }
  };

  const frontendOptions: CheckboxOption[] = [
    {
      value: 'React',
      label: 'React (.js, .jsx, and .tsx)',
      formValue: 'React'
    },
    {
      value: 'Vue',
      label: 'Vue (.vue)',
      formValue: 'Vue'
    },
    {
      value: 'Angular',
      label: 'Angular (.component.html)',
      formValue: 'Angular'
    },
    {
      value: 'HTML',
      label: 'HTML (.html)',
      formValue: 'HTML'
    },
    {
      value: 'Markdown',
      label: 'Markdown (.md and .markdown)',
      formValue: 'Markdown'
    }
  ];

  const ideOptions: CheckboxOption[] = [
    {
      value: 'VS Code',
      label: 'VS Code',
      formValue: 'VS Code'
    },
    {
      value: 'IDEA IntelliJ Ultimate',
      label: 'IDEA IntelliJ Ultimate',
      formValue: 'IDEA IntelliJ Ultimate'
    },
    {
      value: 'WebStorm',
      label: 'WebStorm',
      formValue: 'WebStorm'
    }
  ];

  const sourceControlOptions: CheckboxOption[] = [
    {
      value: 'Git',
      label: 'Git',
      formValue: 'Git'
    },
    {
      value: 'Mercurial',
      label: 'Mercurial',
      formValue: 'Mercurial'
    },
    {
      value: 'Subversion',
      label: 'Subversion',
      formValue: 'Subversion'
    }
  ];

  const sourceControlProviderOptions: CheckboxOption[] = [
    {
      value: 'GitHub',
      label: 'GitHub',
      formValue: 'GitHub'
    },
    {
      value: 'GitLab',
      label: 'GitLab',
      formValue: 'GitLab'
    },
    {
      value: 'Bitbucket',
      label: 'Bitbucket',
      formValue: 'Bitbucket'
    },
    {
      value: 'AWS CodeCommit',
      label: 'AWS CodeCommit',
      formValue: 'AWS CodeCommit'
    },
    {
      value: 'Self Hosted',
      label: t('Self Hosted'),
      formValue: 'Self Hosted'
    }
  ];

  const testingFrameworkOptions: CheckboxOption[] = [
    {
      value: 'Behave',
      label: 'Behave',
      formValue: 'Behave'
    },
    {
      value: 'Capybara',
      label: 'Capybara',
      formValue: 'Capybara'
    },
    {
      value: 'Cucumber (Java)',
      label: 'Cucumber (Java)',
      formValue: 'Cucumber (Java)'
    },
    {
      value: 'Cucumber (Python)',
      label: 'Cucumber (Python)',
      formValue: 'Cucumber (Python)'
    },
    {
      value: 'Cucumber (Ruby)',
      label: 'Cucumber (Ruby)',
      formValue: 'Cucumber (Ruby)'
    },
    {
      value: 'Cypress',
      label: 'Cypress',
      formValue: 'Cypress'
    },
    {
      value: 'Gradle (JUnit)',
      label: 'Gradle (JUnit)',
      formValue: 'Gradle (JUnit)'
    },
    {
      value: 'Gradle (TestNG)',
      label: 'Gradle (TestNG)',
      formValue: 'Gradle (TestNG)'
    },
    {
      value: 'Hamcrest',
      label: 'Hamcrest',
      formValue: 'Hamcrest'
    },
    {
      value: 'Jest',
      label: 'Jest',
      formValue: 'Jest'
    },
    {
      value: 'MSTest',
      label: 'MSTest',
      formValue: 'MSTest'
    },
    {
      value: 'NUnit',
      label: 'NUnit',
      formValue: 'NUnit'
    },
    {
      value: 'Playwright (Java)',
      label: 'Playwright (Java)',
      formValue: 'Playwright (Java)'
    },
    {
      value: 'Playwright (JavaScript)',
      label: 'Playwright (JavaScript)',
      formValue: 'Playwright (JavaScript)'
    },
    {
      value: 'Puppeteer',
      label: 'Puppeteer',
      formValue: 'Puppeteer'
    },
    {
      value: 'PyTest',
      label: 'PyTest',
      formValue: 'PyTest'
    },
    {
      value: 'Robot',
      label: 'Robot',
      formValue: 'Robot'
    },
    {
      value: 'Selenium (C#)',
      label: 'Selenium (C#)',
      formValue: 'Selenium (C#)'
    },
    {
      value: 'Selenium (Java)',
      label: 'Selenium (Java)',
      formValue: 'Selenium (Java)'
    },
    {
      value: 'Selenium (Python)',
      label: 'Selenium (Python)',
      formValue: 'Selenium (Python)'
    },
    {
      value: 'Selenium (Ruby)',
      label: 'Selenium (Ruby)',
      formValue: 'Selenium (Ruby)'
    },
    {
      value: 'TestCafe',
      label: 'TestCafe',
      formValue: 'TestCafe'
    },
    {
      value: 'TestNG',
      label: 'TestNG',
      formValue: 'TestNG'
    },
    {
      value: 'Unittest',
      label: 'Unittest',
      formValue: 'Unittest'
    },
    {
      value: 'Watir',
      label: 'Watir',
      formValue: 'Watir'
    },
    {
      value: 'WebDriverIO (WDIO)',
      label: 'WebDriverIO (WDIO)',
      formValue: 'WebDriverIO (WDIO)'
    },
    {
      value: 'WebDriverIO (WDJS)',
      label: 'WebDriverIO (WDJS)',
      formValue: 'WebDriverIO (WDJS)'
    }
  ];

  const ciCdPlatformOptions: CheckboxOption[] = [
    {
      value: 'Jenkins',
      label: 'Jenkins',
      formValue: 'Jenkins'
    },
    {
      value: 'SonarQube',
      label: 'SonarQube',
      formValue: 'SonarQube'
    },
    {
      value: 'GitHub Actions',
      label: 'GitHub Actions',
      formValue: 'GitHub Actions'
    },
    {
      value: 'CircleCI',
      label: 'CircleCI',
      formValue: 'CircleCI'
    },
    {
      value: 'Azure Pipelines',
      label: 'Azure Pipelines',
      formValue: 'Azure Pipelines'
    }
  ];

  const heading = t('A11y CI/CD Tools Assignment Form');

  return (
    <form
      className={styles.onboardingForm}
      onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}
    >
      {submitting && <ScrimmedLoader role="alert" label={t('Submitting')} />}
      <PageHeader heading={heading} />
      <FieldWrap>
        <div className={styles.formGroup}>
          <Controller
            control={control}
            name="productName"
            render={({ field }) => (
              <TextField
                label={t('ADP product name')}
                description={t(
                  'This comes from the ADP Ally Team list on SharePoint'
                )}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                required
              />
            )}
          />
          <Controller
            control={control}
            name="teamName"
            render={({ field }) => (
              <TextField
                label={t('Team name')}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <FieldGroup
          className={styles.fieldGroup}
          label={
            <>
              {t('Are you, or will you be on One CI/CD?')}
              <span className="Field__required-text"> ({t('Required')})</span>
            </>
          }
          error={errors.onOneCiCd?.message}
        >
          <Controller
            control={control}
            name="onOneCiCd"
            rules={{
              required: t('Please select an option for One CI/CD')
            }}
            render={({ field }) => (
              <RadioGroup
                ref={onOneCiCdRef}
                tabIndex={-1}
                name={field.name}
                value={field.value}
                onChange={(radio: RadioItem) => {
                  field.onChange(radio.value);
                }}
                radios={onOneCiCdOptions}
              />
            )}
          />
        </FieldGroup>
        <fieldset>
          <legend>
            {t('What do you use to develop your web application’s frontend?')}
          </legend>
          <CheckboxGroup
            control={control}
            name="frontend"
            options={frontendOptions}
            fieldId="frontend"
          />
          <Controller
            control={control}
            name="otherFrontend"
            render={({ field }) => (
              <TextField
                label={t('Other Frontend')}
                name="otherFrontend"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </fieldset>
        <fieldset>
          <legend>{t('What IDE(s) do you use for development?')}</legend>
          <CheckboxGroup
            control={control}
            name="ide"
            options={ideOptions}
            fieldId="ide"
          />
          <Controller
            control={control}
            name="otherIde"
            render={({ field }) => (
              <TextField
                label={t('Other IDE')}
                name="otherIde"
                onChange={field.onChange}
              />
            )}
          />
        </fieldset>
        <fieldset>
          <legend>{t('What source control system(s) do you use?')}</legend>
          <CheckboxGroup
            control={control}
            name="sourceControl"
            options={sourceControlOptions}
            fieldId="sourceControl"
          />
          <Controller
            control={control}
            name="otherSourceControl"
            render={({ field }) => (
              <TextField
                label={t('Other Source Control')}
                name="otherSourceControl"
                onChange={field.onChange}
              />
            )}
          />
        </fieldset>
        <fieldset>
          <legend>{t('What source control provider(s) do you use?')}</legend>
          <CheckboxGroup
            control={control}
            name="sourceControlProvider"
            options={sourceControlProviderOptions}
            fieldId="sourceControlProvider"
          />
          <Controller
            control={control}
            name="otherSourceControlProvider"
            render={({ field }) => (
              <TextField
                label={t('Other Source Control Provider')}
                name="otherSourceControlProvider"
                onChange={field.onChange}
              />
            )}
          />
        </fieldset>
        <fieldset>
          <legend>{t('What testing framework(s) do you use?')}</legend>
          <CheckboxGroup
            control={control}
            name="testingFramework"
            options={testingFrameworkOptions}
            fieldId="nodeTestingFramework"
          />
          <Controller
            control={control}
            name="otherTestingFramework"
            render={({ field }) => (
              <TextField
                label={t('Other Testing Framework')}
                name="otherTestingFramework"
                onChange={field.onChange}
              />
            )}
          />
        </fieldset>
        <fieldset>
          <legend>{t('What CI/CD systems, if any, do your teams use?')}</legend>
          <CheckboxGroup
            control={control}
            name="ciCdPlatform"
            options={ciCdPlatformOptions}
            fieldId="ciCdPlatform"
          />
          <Controller
            control={control}
            name="otherCiCdPlatform"
            render={({ field }) => (
              <TextField
                label={t('Other CI/CD Platform')}
                name="otherCiCdPlatform"
                onChange={field.onChange}
              />
            )}
          />
        </fieldset>
        <Button type="submit">{t('Submit')}</Button>
        <Link className="Button--secondary" to="/">
          {t('Cancel')}
        </Link>
      </FieldWrap>
    </form>
  );
};

export default OnboardingForm;
